/**
 * Testimonials component - Grid layout with 6 testimonials (2 rows × 3 columns)
 * Clean, visually appealing cards with user photos and ratings
 */

import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {Card} from '../ui/Card';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  rating: number;
}

const testimonials: Testimonial[] = [
  {
    quote: 'VoxaPilot transformed how we run meetings. The AI summaries save us hours every week.',
    author: 'Sarah Johnson',
    role: 'CEO',
    company: 'TechCorp',
    rating: 5,
  },
  {
    quote: 'The transcription accuracy is incredible. We use it for all our client calls.',
    author: 'Michael Chen',
    role: 'Sales Director',
    company: 'StartupXYZ',
    rating: 5,
  },
  {
    quote: 'Best investment we made this year. The team collaboration features are outstanding.',
    author: 'Emily Rodriguez',
    role: 'VP of Operations',
    company: 'BigCo',
    rating: 5,
  },
  {
    quote: 'Game changer for remote teams. We never miss important details anymore.',
    author: 'David Kim',
    role: 'Engineering Manager',
    company: 'DevCorp',
    rating: 5,
  },
  {
    quote: 'The search functionality is amazing. Finding specific moments in past meetings is instant.',
    author: 'Lisa Wang',
    role: 'Product Manager',
    company: 'InnovateCo',
    rating: 5,
  },
  {
    quote: 'Our meeting productivity has increased significantly. Highly recommend!',
    author: 'James Taylor',
    role: 'Operations Lead',
    company: 'ScaleUp',
    rating: 5,
  },
];

export function Testimonials() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="testimonials" padding="lg" background="white" ref={ref} className="bg-background dark:bg-[#1E1E1E]">
      <Container>
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
            Testimonials
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
            Trusted by Teams Everywhere
          </h2>
          <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585]">
            See how teams are transforming their meetings
          </p>
        </div>

        {/* Testimonials Grid - 2 rows × 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.author}
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{transitionDelay: `${index * 0.1}s`}}
            >
              <Card className="p-6 h-full transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/50 hover:-translate-y-1 border border-gray-200 dark:border-gray-700">
                <div className="space-y-5">
                  {/* Rating */}
                  <div className="flex items-center">
                    {Array.from({length: testimonial.rating}).map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                    "{testimonial.quote}"
                  </p>

                  {/* Author */}
                  <div className="pt-4 border-t border-border dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      {/* User Photo Placeholder */}
                      <div className="w-10 h-10 bg-[#1E1E1E] dark:bg-[#1E1E1E] rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 border border-gray-200 dark:border-gray-700">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-base text-textPrimary dark:text-[#CCCCCC]">
                          {testimonial.author}
                        </p>
                        <p className="text-sm text-textTertiary dark:text-[#6A6A6A]">
                          {testimonial.role} at {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
